import React, { useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import './ConfirmModal.css';

/**
 * Reusable in-app confirmation modal — replaces native window.confirm().
 *
 * Props:
 *   isOpen        boolean — control visibility
 *   title         string — header text (e.g. "Delete project?")
 *   message       string|node — body text
 *   confirmLabel  string — confirm button text (default "Confirm")
 *   cancelLabel   string — cancel button text (default "Cancel")
 *   onConfirm     fn — called when confirm is clicked
 *   onCancel      fn — called when cancel/backdrop/Esc dismisses
 *   destructive   boolean — when true, confirm button is red
 *   requireText   string — when set, Confirm (and Enter) work only after the
 *                 writer types this word; for actions that delete their work
 *   requireLabel  string — label for that field
 *   detail        string — second body line, after message
 *   alternateLabel string — second action. Enter still runs the first action.
 *   confirmNote   string — hint under or beside the first action
 *   onAlternate   fn — called for the second action
 */
function ConfirmModal({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  onAlternate,
  destructive = false,
  requireText = '',
  requireLabel = '',
  alternateLabel = '',
  confirmNote = '',
  detail = '',
}) {
  const [typed, setTyped] = useState('');
  const noteId = useId();
  const choiceMode = Boolean(alternateLabel);
  const unlocked = !requireText
    || typed.trim().toLowerCase() === String(requireText).trim().toLowerCase();

  useEffect(() => {
    if (!isOpen) setTyped('');
  }, [isOpen]);

  // Esc cancels. Enter runs the first action, which is the safe default
  // when a second action is offered. The second action is its own button.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (!choiceMode) {
        if (e.key === 'Escape') onCancel?.();
        if (e.key === 'Enter' && unlocked) onConfirm?.();
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel?.();
        return;
      }
      if (e.key !== 'Enter' || !unlocked) return;
      if (e.target?.closest?.('[data-cm-alternate]')) return;
      if (e.target?.closest?.('[data-cm-cancel]')) return;
      e.preventDefault();
      onConfirm?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onCancel, onConfirm, unlocked, choiceMode]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onCancel?.();
  };

  return createPortal(
    <div className="cm-backdrop" onClick={handleBackdropClick} role="dialog" aria-modal="true">
      <div className="cm-modal">
        {title && <h3 className="cm-title">{title}</h3>}
        {message && !detail && <div className="cm-message">{message}</div>}
        {message && detail && (
          <div className="cm-message">
            <p className="cm-message-line">{message}</p>
            <p className="cm-message-line">{detail}</p>
          </div>
        )}
        {requireText && (
          <label className="cm-require">
            <span className="cm-require__label">{requireLabel || `Type ${requireText} to confirm`}</span>
            <input
              type="text"
              className="cm-require__input"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              autoFocus
            />
          </label>
        )}
        {choiceMode ? (
          <div className="cm-choices">
            <div className="cm-choice">
              <button
                type="button"
                className="cm-btn cm-btn--primary"
                onClick={onConfirm}
                disabled={!unlocked}
                autoFocus={!requireText}
                aria-describedby={confirmNote ? noteId : undefined}
              >
                {confirmLabel}
              </button>
              {confirmNote ? (
                <p id={noteId} className="cm-choice-note">{confirmNote}</p>
              ) : null}
            </div>
            <button
              type="button"
              className="cm-btn cm-btn--destructive"
              data-cm-alternate=""
              onClick={onAlternate}
              disabled={!unlocked}
            >
              {alternateLabel}
            </button>
            <button
              type="button"
              className="cm-btn cm-btn--cancel"
              data-cm-cancel=""
              onClick={onCancel}
            >
              {cancelLabel}
            </button>
          </div>
        ) : (
          <div className="cm-actions">
            <button type="button" className="cm-btn cm-btn--cancel" onClick={onCancel}>
              {cancelLabel}
            </button>
            <button
              type="button"
              className={`cm-btn ${destructive ? 'cm-btn--destructive' : 'cm-btn--primary'}`}
              onClick={onConfirm}
              disabled={!unlocked}
              autoFocus={!requireText}
            >
              {confirmLabel}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

export default ConfirmModal;
